// Shared across every screen: nav, app shell chrome, status labels shared
// with StatusChip, and generic action/state words reused by several pages.
export const en = {
  "nav.home": "Home",
  "nav.documents": "Documents",
  "nav.clients": "Clients",
  "nav.catalog": "Catalog",
  "nav.reports": "Reports",
  "nav.account": "Account",
  "shell.search.clear": "Clear search",
  "shell.search.results": "{count} MATCHES",
  "search.noResults.title": "No matches",
  "search.noResults.body": "Nothing matches “{query}”.",
  "search.noResults.action": "Clear search",
  "shell.newDocument": "New Document",
  "shell.storageUsed": "{count} DOCS · {used} / {quota}",
  "shell.logout": "Log out",
  "shell.language": "Language",
  "status.all": "All",
  "status.draft": "Draft",
  "status.pending": "Pending",
  "status.accepted": "Accepted",
  "status.overdue": "Overdue",
  "status.closed": "Closed",
  "status.void": "Void",
  "status.superseded": "Superseded",
  "status.invoiced": "Invoiced",
  "action.viewAll": "View all",
  "action.back": "Back",
  "action.save": "Save",
  "state.loading": "Loading…",
  "common.notBuiltYet": "Not built yet",
  "common.notTrackedYet": "Not tracked yet",
  "common.comingSoon": "Coming soon.",
  "common.loadError.title": "Couldn't load this",
  "common.loadError.body": "Something went wrong loading this page.",
  "common.loadError.retry": "Retry",
  "legal.terms": "Terms",
  "legal.privacy": "Privacy",
  "legal.support": "Support",
  "legal.title": "Legal",
  "legal.help": "The agreement you accepted at sign-up and how we handle your data.",
  // Per-route <title>/meta description (backlog LAUNCH-28) — copy from a
  // Lovable review pass, verified against the real Terms/Privacy/Support
  // page content before shipping (see that item's notes).
  "terms.meta.title": "Terms of Service — SnapDraw for Contractors",
  "terms.meta.desc":
    "The agreement for your SnapDraw account: 14-day trial and billing, acceptable use, data ownership and retention, and liability limits.",
  "privacy.meta.title": "Privacy Policy — SnapDraw for Contractors",
  "privacy.meta.desc":
    "What SnapDraw stores in your account, how it's protected, how long we keep it, which third-party services process it, and how to export or delete it.",
  "support.meta.title": "Support & Help — SnapDraw for Contractors",
  "support.meta.desc":
    "Get help with SnapDraw: the 14-day free trial, billing and cancellation, exporting your quotes and invoices, and deleting your account.",
  "shell.trialBanner.singular": "Free trial — {days} day left",
  "shell.trialBanner.plural": "Free trial — {days} days left",
  "shell.subscribeNow": "Subscribe now",
  "shell.graceBanner.singular": "Payment lapsed — {days} day left of read-only access",
  "shell.graceBanner.plural": "Payment lapsed — {days} days left of read-only access",
  "shell.updatePayment": "Update payment method",
  "shell.askOwnerToUpdatePayment": "Ask your account owner to update payment",
  "shell.exportData": "Export your data",
  "shell.exportingData": "Exporting…",
  "shell.updateBanner.body": "A newer version of SnapDraw is available.",
  "shell.updateBanner.dismiss": "Dismiss",
  "grace.notice.title": "This isn't available during your read-only grace period.",
  "grace.notice.backLink": "Back to your documents",
  "update.required.title": "Update required",
  "update.required.body": "This version of SnapDraw is no longer supported. Please update to continue.",
  "update.required.cta": "Update now",
  "update.required.storeHint": "Update from the App Store or Play Store to continue.",
} as const;

export const es: Record<keyof typeof en, string> = {
  "nav.home": "Inicio",
  "nav.documents": "Documentos",
  "nav.clients": "Clientes",
  "nav.catalog": "Catálogo",
  "nav.reports": "Reportes",
  "nav.account": "Cuenta",
  "shell.search.clear": "Borrar búsqueda",
  "shell.search.results": "{count} RESULTADOS",
  "search.noResults.title": "Sin resultados",
  "search.noResults.body": "Nada coincide con “{query}”.",
  "search.noResults.action": "Borrar búsqueda",
  "shell.newDocument": "Nuevo documento",
  "shell.storageUsed": "{count} DOCS · {used} / {quota}",
  "shell.logout": "Cerrar sesión",
  "shell.language": "Idioma",
  "status.all": "Todos",
  "status.draft": "Borrador",
  "status.pending": "Pendiente",
  "status.accepted": "Aceptado",
  "status.overdue": "Vencido",
  "status.closed": "Cerrado",
  "status.void": "Anulado",
  "status.superseded": "Reemplazada",
  "status.invoiced": "Facturada",
  "action.viewAll": "Ver todo",
  "action.back": "Volver",
  "action.save": "Guardar",
  "state.loading": "Cargando…",
  "common.notBuiltYet": "Aún no disponible",
  "common.notTrackedYet": "Aún no se rastrea",
  "common.comingSoon": "Próximamente.",
  "common.loadError.title": "No pudimos cargar esto",
  "common.loadError.body": "Ocurrió un error al cargar esta página.",
  "common.loadError.retry": "Reintentar",
  "legal.terms": "Términos",
  "legal.privacy": "Privacidad",
  "legal.support": "Soporte",
  "legal.title": "Legal",
  "legal.help": "El acuerdo que aceptaste al registrarte y cómo tratamos tus datos.",
  "terms.meta.title": "Términos del Servicio — SnapDraw for Contractors",
  "terms.meta.desc":
    "El acuerdo de tu cuenta SnapDraw: prueba gratis de 14 días, facturación, uso aceptable, propiedad y conservación de tus datos, y límites de responsabilidad.",
  "privacy.meta.title": "Política de Privacidad — SnapDraw for Contractors",
  "privacy.meta.desc":
    "Qué datos guarda SnapDraw en tu cuenta, cómo los protege, cuánto tiempo los conserva, qué servicios externos los procesan y cómo exportarlos o borrarlos.",
  "support.meta.title": "Soporte y Ayuda — SnapDraw for Contractors",
  "support.meta.desc":
    "Ayuda con SnapDraw: la prueba gratis de 14 días, facturación y cancelación, exportar tus cotizaciones y facturas, y eliminar tu cuenta.",
  "shell.trialBanner.singular": "Prueba gratuita — queda {days} día",
  "shell.trialBanner.plural": "Prueba gratuita — quedan {days} días",
  "shell.subscribeNow": "Suscribirme ahora",
  "shell.graceBanner.singular": "Pago vencido — queda {days} día de acceso de solo lectura",
  "shell.graceBanner.plural": "Pago vencido — quedan {days} días de acceso de solo lectura",
  "shell.updatePayment": "Actualizar método de pago",
  "shell.askOwnerToUpdatePayment": "Pídele al propietario de la cuenta que actualice el pago",
  "shell.exportData": "Exportar tus datos",
  "shell.exportingData": "Exportando…",
  "shell.updateBanner.body": "Hay una nueva versión de SnapDraw disponible.",
  "shell.updateBanner.dismiss": "Descartar",
  "grace.notice.title": "Esto no está disponible durante tu período de gracia de solo lectura.",
  "grace.notice.backLink": "Volver a tus documentos",
  "update.required.title": "Actualización requerida",
  "update.required.body": "Esta versión de SnapDraw ya no es compatible. Actualiza para continuar.",
  "update.required.cta": "Actualizar ahora",
  "update.required.storeHint": "Actualiza desde App Store o Play Store para continuar.",
};
